import { Link } from "react-router-dom";
import { formatLoadDate } from "../loads/date";
import { useLiveQuery } from "dexie-react-hooks";
import { formatUsd, analyze } from "@haulsheet/core";
import { getDb } from "../db/dexie";
import { contractForLoad } from "../db/contracts";
import { buildAnalyzeInput } from "../hooks/useAnalyzeLoad";
import { useAuthStore } from "../store/authStore";
import { useNow } from "../hooks/useNow";
import { formatElapsed } from "../logs/detention";
import { STATUS_LABEL } from "../loads/lifecycle";

export function LoadsScreen() {
  const me = useAuthStore((s) => s.me);
  const rows = useLiveQuery(async () => {
    const db = getDb();
    const loads = await db.load
      .filter((l) => l.deletedAt == null)
      .toArray();
    loads.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    const contracts = await db.pay_contract.toArray();
    const costs = await db.cost_profile.toArray();
    const trucks = await db.truck.toArray();
    const cost =
      costs.find((c) => c.driverId === me?.driverId) ?? costs[0] ?? null;
    const truck = trucks[0] ?? null;
    return loads.map((load) => ({
      load,
      pay: analyze(
        buildAnalyzeInput(load, contractForLoad(load, contracts), cost, truck),
      ),
    }));
  }, [me?.driverId]);
  // Running detention clocks (departedAt null). Linked ones badge their load's
  // row; one not tied to any load gets its own line at the top.
  const running = useLiveQuery(
    async () => (await getDb().detention.toArray()).filter((d) => d.deletedAt == null && d.departedAt == null),
    [],
  );
  const now = useNow((running ?? []).length > 0);
  const runningByLoad = new Map((running ?? []).filter((d) => d.loadId).map((d) => [d.loadId!, d]));
  const loadIds = new Set((rows ?? []).map((r) => r.load.id));
  const unlinked = (running ?? []).filter((d) => !d.loadId || !loadIds.has(d.loadId));
  const elapsed = (arrivedAt: string) => formatElapsed(now - new Date(arrivedAt).getTime());

  return (
    <div className="stack">
      <h1 className="screen-title">Loads</h1>
      <div className="card" style={{ padding: 0 }}>
        {unlinked.map((d) => (
          <Link key={d.id} to={`/logs/detention?id=${d.id}`} className="list-item" data-testid="detention-unlinked">
            <div>
              <div>
                Detention running{d.placeText ? ` at ${d.placeText}` : ""}{" "}
                <span className="chip chip-warn" data-testid="detention-badge-elapsed">
                  {elapsed(d.arrivedAt)}
                </span>
              </div>
              <div className="muted">Not tied to a load</div>
            </div>
          </Link>
        ))}
        {(rows ?? []).length === 0 && unlinked.length === 0 ? (
          <p className="muted" style={{ padding: "1rem" }}>
            No loads yet. Start on Check.
          </p>
        ) : (
          (rows ?? []).map(({ load, pay }) => (
            <Link
              key={load.id}
              to={`/loads/${load.id}`}
              className="list-item"
              data-testid="load-row"
            >
              <div>
                <div>
                  {load.origin.text || "Origin"} →{" "}
                  {load.destination.text || "Destination"}
                </div>
                <div className="muted">
                  <span className="chip">{STATUS_LABEL[load.status] ?? load.status}</span>{" "}
                  {runningByLoad.has(load.id) ? (
                    <>
                      <span className="chip chip-warn" data-testid="detention-badge" aria-label="Detention running">
                        Detention <span data-testid="detention-badge-elapsed">{elapsed(runningByLoad.get(load.id)!.arrivedAt)}</span>
                      </span>{" "}
                    </>
                  ) : null}
                  {formatUsd(pay.keepCents)} keep
                  {formatLoadDate(load.loadDate) ? ` · ${formatLoadDate(load.loadDate)}` : ""}
                </div>
              </div>
              <div className="muted">{formatUsd(load.linehaulCents)}</div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
